import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { DatePicker } from '../ui/date-picker'
import { Alert, AlertDescription } from '../ui/alert'
import { Loader2 } from 'lucide-react'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { EventRequest } from '../../requests/EventRequest'

interface AddEventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const AddEventDialog: React.FC<AddEventDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate()
  const { getActiveClients, createEvent } = useApplicationStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [formData, setFormData] = useState<EventRequest>({
    clientId: 0,
    name: '',
    startDate: null as any,
    endDate: null as any,
  })
  
  const [validation, setValidation] = useState({
    clientId: false,
    name: false,
    startDate: false,
    endDate: false,
    dateRange: false,
  })

  const activeClients = getActiveClients()

  useEffect(() => {
    if (open && activeClients.length > 0 && formData.clientId === 0) {
      setFormData(prev => ({ ...prev, clientId: activeClients[0].id }))
    }
  }, [open, activeClients])

  const handleReset = () => {
    setFormData({
      clientId: activeClients.length > 0 ? activeClients[0].id : 0,
      name: '',
      startDate: null as any,
      endDate: null as any,
    })
    setValidation({
      clientId: false,
      name: false,
      startDate: false,
      endDate: false,
      dateRange: false,
    })
    setError(null)
  }

  const handleClose = () => {
    handleReset()
    onOpenChange(false)
  }

  const validateForm = () => {
    const newValidation = {
      clientId: formData.clientId === 0,
      name: formData.name.trim() === '',
      startDate: !formData.startDate,
      endDate: !formData.endDate,
      dateRange: false,
    }

    // Check if end date is after start date
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate)
      const end = new Date(formData.endDate)
      newValidation.dateRange = end < start
    }

    setValidation(newValidation)
    
    return !Object.values(newValidation).some(v => v)
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const event = await createEvent(formData)
      handleClose()
      // Navigate to the event configuration page
      navigate(`/event/${event.id}/config`)
    } catch (err: any) {
      console.error('Failed to create event:', err)
      setError(err.message || 'Failed to create event. Please try again.')
      setLoading(false)
    }
  }

  if (activeClients.length === 0 && open) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cannot Create Event</DialogTitle>
          </DialogHeader>
          <Alert>
            <AlertDescription>
              This operation cannot be completed as there are no clients associated with the current user.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Event</DialogTitle>
          <DialogDescription>
            Fill in the details below to create a new event.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-2">
            <Label htmlFor="client">
              Client <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.clientId.toString()}
              onValueChange={(value) => {
                setFormData(prev => ({ ...prev, clientId: parseInt(value) }))
                setValidation(prev => ({ ...prev, clientId: false }))
              }}
              disabled={loading}
            >
              <SelectTrigger 
                id="client"
                className={validation.clientId ? 'border-red-500' : ''}
              >
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent>
                {activeClients.map(client => (
                  <SelectItem key={client.id} value={client.id.toString()}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validation.clientId && (
              <p className="text-sm text-red-500">Please select a client</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="name">
              Event Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }))
                setValidation(prev => ({ ...prev, name: false }))
              }}
              placeholder="Enter event name"
              disabled={loading}
              className={validation.name ? 'border-red-500' : ''}
            />
            {validation.name && (
              <p className="text-sm text-red-500">Event name is required</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="startDate">
              Start Date <span className="text-red-500">*</span>
            </Label>
            <DatePicker
              date={formData.startDate}
              onDateChange={(date) => {
                setFormData(prev => ({ ...prev, startDate: date || null as any }))
                setValidation(prev => ({ ...prev, startDate: false, dateRange: false }))
              }}
              placeholder="Select start date"
              disabled={loading}
              className={validation.startDate ? 'border-red-500' : ''}
            />
            {validation.startDate && (
              <p className="text-sm text-red-500">Start date is required</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="endDate">
              End Date <span className="text-red-500">*</span>
            </Label>
            <DatePicker
              date={formData.endDate}
              onDateChange={(date) => {
                setFormData(prev => ({ ...prev, endDate: date || null as any }))
                setValidation(prev => ({ ...prev, endDate: false, dateRange: false }))
              }}
              placeholder="Select end date"
              disabled={loading}
              className={validation.endDate || validation.dateRange ? 'border-red-500' : ''}
            />
            {validation.endDate && (
              <p className="text-sm text-red-500">End date is required</p>
            )}
            {validation.dateRange && (
              <p className="text-sm text-red-500">End date must be after start date</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Event'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}